import { forwardRef } from 'react'

import { colors, Icon, Select, colors as zetColors } from '@zet/design-system'
import { Stack, XStack } from '@zet/design-system/layout'
import { B4, B5, B6, CTN, SH3 } from '@zet/design-system/typography'

import { useApiAction } from '../../../api/useApiAction'
import { brandsI18n } from '../../../localization'
import { digitalKycSteps, radioStepperRecommendation } from '../../../utils'

interface Props {
  label?: string
  onChange: (...props: any) => void
  onBlur: (...props: any) => void
  isRequired?: boolean
  fieldOptions: { optionValue: string; optionId: any; icon?: string }[]
  value: any
  errorMessage?: string
  ocFieldCode?: string
}

export const RadioButtonStepper: React.FC<Props> = forwardRef(
  (
    {
      label,
      onChange,
      onBlur,
      value,
      errorMessage,
      isRequired,
      fieldOptions,
      ocFieldCode,
    },
    ref
  ) => {
    const { data } = useApiAction()

    const findRecommendedOption = (value: string) => {
      return (
        radioStepperRecommendation[ocFieldCode || '']?.recommended === value
      )
    }

    const findKycSteps = (value: string) => {
      return (
        radioStepperRecommendation?.[ocFieldCode || '']?.[value] ??
        digitalKycSteps
      )
    }

    return (
      <>
        <Stack>
          <Stack>
            <Select
              value={value}
              mode={'single'}
              onChange={(value) => {
                onChange(value)
                onBlur()
              }}
            >
              {fieldOptions?.map((item, index) => (
                <Select.Option
                  key={item?.optionId}
                  name={item?.optionId}
                  displayText={item?.optionValue}
                  selectedValue={value}
                  stylingOverrides={{ innerContainer: 'w-full' }}
                  optionToggleContainerStyle={'items-start w-full'}
                  optionTextStyle={'w-[100%]'}
                  optionContainerStyle={`mt-3 w-[100%]  ${
                    value === item?.optionId
                      ? 'border border-primary-50'
                      : 'border border-grays-40'
                  } items-start justify-center rounded-lg`}
                  showToggle={false}
                  iconColor={{
                    active: zetColors.primary[50],
                    inActive: zetColors.grays[60],
                  }}
                  backgroundColor={{
                    active: 'bg-white',
                    inActive: 'bg-white',
                  }}
                  textColor={{
                    active: 'text-primary-200',
                    inActive: 'text-grays-80',
                  }}
                >
                  <Stack className="w-[100%]">
                    <XStack className="bg-primary-10 justify-between rounded-tl-lg rounded-tr-lg p-[12px]">
                      <XStack className="items-center">
                        <Icon
                          type={
                            item.optionId === value ? 'RadioOn' : 'RadioOff'
                          }
                          color={
                            item.optionId === value
                              ? zetColors.primary[50]
                              : zetColors.grays[60]
                          }
                          className={`mr-2`}
                        />
                        <SH3
                          className={`${
                            value === item?.optionId
                              ? 'text-primary-200'
                              : 'text-grays-80'
                          }`}
                        >
                          {item.optionValue}
                        </SH3>
                      </XStack>
                      {findRecommendedOption(item?.optionId) && (
                        <CTN className="text-primary-150 border-primary-50 mb-1 mr-3 rounded-full border bg-white px-2">
                          Recommended
                        </CTN>
                      )}
                    </XStack>
                    <Stack className="p-2 px-3">
                      <B4>{brandsI18n.t('hdfc.steps')}</B4>
                      <Stack className="pl-2">
                        <Stack className="border-grays-30 absolute left-3 ml-2 mt-6 h-[50%] border"></Stack>
                        {findKycSteps(item?.optionId)?.map(
                          (
                            step: {
                              icon: string
                              title: string
                              subTitle: string
                            },
                            index: number
                          ) => {
                            const Icon = step.icon
                            return (
                              <>
                                <XStack
                                  key={index}
                                  className="items-start py-2"
                                >
                                  <Stack className="bg-grays-10 border-grays-30 items-start rounded-full border p-1">
                                    <Icon />
                                  </Stack>
                                  <Stack className="ml-2">
                                    <B4 className="text-bold text-grays-70">
                                      {step.title}
                                    </B4>
                                    <B6 className="text-grays-70">
                                      {step.subTitle}
                                    </B6>
                                  </Stack>
                                </XStack>
                              </>
                            )
                          }
                        )}
                      </Stack>
                      {index === 0 && !!data?.metadata?.daysRemaining && (
                        <XStack className="border-grays-40 mt-2 items-center border-t border-dashed p-1">
                          <Icon
                            type="Timeline"
                            size={16}
                            color={colors.warning[150]}
                          />
                          <CTN className="ml-2">
                            {brandsI18n.t('hdfc.daysLeft', {
                              days: data?.metadata?.daysRemaining,
                              s: data?.metadata?.daysRemaining > 1 ? 's' : '',
                            })}
                          </CTN>
                        </XStack>
                      )}
                    </Stack>
                  </Stack>
                </Select.Option>
              ))}
            </Select>
          </Stack>
          {!!errorMessage && <B5 className="text-error-100">{errorMessage}</B5>}
        </Stack>
      </>
    )
  }
)
